import React, { useState, useEffect } from "react";
import { Button } from "../../elements/Button/Button";
import { Paragraph } from "../../elements/Paragraph/Paragraph";
import AnimatedPulse from "../AnimatedPulse/AnimatedPulse";
import { useHubSpotForm } from "../../../hooks/useHubSpotForm";

export const ShopifyTCO = ({ settings }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const { globals } = settings.settings || {};

  useEffect(() => {
    // Handle responsive changes
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    // Initialize
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Extract settings
  const { section = {}, blocks = [] } = settings.settings || {};
  const statsBlocks = blocks.filter(block => block.type === 'stat_circle');

  // Use the global HubSpot form hook
  const { openForm } = useHubSpotForm({
    portalId: section.hubspot_portal_id,
    formId: section.hubspot_form_id,
    region: section.hubspot_region,
  });
  
  // Mobile layout handling
  const nextCircle = () => {
    setCurrentIndex((prev) => (prev + 1) % statsBlocks.length);
  };

  const prevCircle = () => {
    setCurrentIndex((prev) => (prev - 1 + statsBlocks.length) % statsBlocks.length);
  };

  // Circle component with animated ring
  const CircleWidget = ({ block, isActive = false, globals }) => {
    const { 
      percentage = 0, 
      circle_text = "", 
      circle_description = "", 
      title_font_style = "",
      title_font_style_mobile = "",
      description_font_style = "",
      description_font_style_mobile = ""
    } = block.settings || {};
    
    // Use mobile styles when on mobile devices
    const titleStyle = isMobile ? title_font_style_mobile : title_font_style;
    const descriptionStyle = isMobile ? description_font_style_mobile : description_font_style;
    
    // SVG circle calculation
    const radius = isMobile ? 65 : 99;
    const strokeWidth = 6;
    const normalizedRadius = radius - strokeWidth;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;
    
    return (
      <div className={`circle-widget relative ${isActive ? 'active' : ''}`}>
        {/* Pulsing rings animation */}
        <div className="pulse-rings absolute w-full h-full z-0 ">
          <AnimatedPulse 
              settings={{
                ...globals.pulse_animation,
                className: "absolute top-0 left-0 w-full h-full"
              }}
          />
        </div>
        
        {/* Main circle with percentage fill */}
        <div className="circle-content relative flex flex-col items-center justify-center">
          <div className="svg-container relative w-full h-full">
            <svg 
              height={radius * 2} 
              width={radius * 2} 
              className="progress-ring"
            >
              {/* Background circle */}
              <circle
                stroke="#C6C9C3"
                fill="transparent"
                strokeWidth={strokeWidth}
                r={normalizedRadius}
                cx={radius}
                cy={radius}
              />
              {/* Progress circle */}
              <circle
                stroke="#899B30"
                fill="transparent"
                strokeWidth={3}
                strokeDasharray={circumference + ' ' + circumference}
                style={{ strokeDashoffset }}
                strokeLinecap="square"
                r={normalizedRadius}
                cx={radius}
                cy={radius}
                className="progress-ring__circle"
                transform={`rotate(-90, ${radius}, ${radius})`}
              />
            </svg>
            <div className="absolute top-[32%] left-[22%] lg:left-[16%] lg:top-1/4 flex flex-col items-center justify-center">
              <h3 className={`text-4xl font-bold ${titleStyle}`}>{circle_text}</h3>
            </div>
          </div>
          <div className="circle-text mt-4 text-center">
            <Paragraph
              text={circle_description}
              type={descriptionStyle}
              classes="text-center max-w-xs mx-auto"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="shopify-tco relative w-full overflow-hidden max-lg:px-3 py-8 md:py-16"
      style={{
        minHeight: isMobile 
          ? `${section.mobile_min_height || 0}px` 
          : `${section.desktop_min_height || 0}px`
      }}
    >
      <div className="container lg:flex lg:flex-row-reverse mx-auto px-4 lg:gap-[122px]">
        {/* Header Section */}
        <div className="basis-[50%] w-full flex flex-col gap-6 lg:justify-center max-lg:mb-10">
          <h2 className={`${section.title_font_style || ''}`}>
            {section.title || 'Shopify Total Cost Of Ownership Calculator'}
          </h2>
          
          <Paragraph
            text={section.description || 'Let MindArc take care of the groundwork, creating a detailed Shopify Business Case and Total Cost of Ownership calculator tailored to your unique needs. With a clear breakdown, you\'ll have everything you need to confidently assess whether Shopify is the right platform for your brand\'s growth.'}
            type={section.description_font_style || ''}
          />
          
          <div>
            {section.cta_text && (
              <Button 
                label={section.cta_text || 'Custom TCO Evaluation'}
                style={section.cta_style || 'primary'}
                onClick={() => openForm()}
                icon={section.cta_icon}
                className="inline-flex items-center cursor-pointer"
              />
            )}
          </div>
        </div>
        
        {/* Desktop Layout */}
        <div className="circles-container flex relative w-full max-w-[351px] lg:max-w-[565px] basis-[45%] h-[505px] lg:h-[719px] max-lg:mx-auto">
          {statsBlocks.map((block, index) => {
            // Define custom positions per index (adjust as needed)
            const mobilePositions = [
              'top-[35px] left-[30px]',
              'top-[164px] right-[9px]',
              'bottom-[63px] left-[56px]'
            ];
            
            const desktopPositions = [
              'lg:top-[95px] lg:left-[105px]',
              'lg:top-[308px] lg:right-[0px]',
              'lg:bottom-[92px] lg:left-[45px]'
            ];

            const mobile = mobilePositions[index] || '';
            const desktop = desktopPositions[index] || '';

            return (
              <div
                key={block.id}
                className={`circle-wrapper absolute ${mobile} ${desktop}`}
              >
                <CircleWidget block={block} globals={globals} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Add styles for the pulsing animations */}
      <style jsx>{`
        .circle-widget {
          width: 198px;
          height: 198px;
          color: var(--color-accent, #4a7d3b);
        }

        @media (max-width: 1023px) {
          .circle-widget {
            width: 130px;
            height: 130px;
          }
        }
        
        .progress-ring__circle {
          transition: stroke-dashoffset 0.5s ease;
          transform: rotate(-90deg);
          transform-origin: 50% 50%;
        }
        
        .pulse-rings {
          pointer-events: none;
        }
        
        .ring {
          position: absolute;
          border-radius: 50%;
          border: 2px solid currentColor;
          opacity: 0;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
        
        .ring-1 {
          width: 100%;
          height: 100%;
          animation: pulse 2s ease-out infinite;
        }
        
        .ring-2 {
          width: 110%;
          height: 110%;
          animation: pulse 2s ease-out 0.7s infinite;
        }
        
        .ring-3 {
          width: 120%;
          height: 120%;
          animation: pulse 2s ease-out 1.4s infinite;
        }
        
        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.1;
          }
          50% {
            opacity: 0.15;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.4);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ShopifyTCO;